import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import publicApi from '../helper/publicApi';
import { getOrderTotals, getItemLineTotal, formatOrderPrice } from '../admin/utils/orderTotals';

const TrackOrder = () => {
    const [orderNumber, setOrderNumber] = useState('');
    const [orderStatus, setOrderStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Map status to step completion
    const getStepsFromStatus = (status, createdAt) => {
        const statusOrder = ['pending', 'processing', 'shipped', 'out_for_delivery', 'delivered'];
        const statusLabels = {
            pending: 'Commande confirmée',
            processing: 'En préparation',
            shipped: 'Expédiée',
            out_for_delivery: 'En cours de livraison',
            delivered: 'Livrée'
        };
        
        const currentIndex = statusOrder.indexOf(status);
        const orderDate = new Date(createdAt);
        
        return statusOrder.map((s, index) => {
            const isCompleted = index <= currentIndex;
            let date = 'En attente';
            
            if (isCompleted) {
                const stepDate = new Date(orderDate);
                stepDate.setDate(stepDate.getDate() + index);
                date = stepDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
            }
            
            return {
                title: statusLabels[s],
                date: date,
                completed: isCompleted
            };
        });
    };

    // Get status label in French
    const getStatusLabel = (status) => {
        const labels = {
            pending: 'Commande confirmée',
            processing: 'En préparation',
            shipped: 'Expédiée',
            out_for_delivery: 'En cours de livraison',
            delivered: 'Livrée',
            cancelled: 'Annulée'
        };
        return labels[status] || status;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setOrderStatus(null);
        
        try {
            const response = await publicApi.get(`/public/orders/${orderNumber.trim()}`);
            const order = response.order;
            
            // Format order date
            const orderDate = new Date(order.created_at);
            const formattedDate = orderDate.toLocaleDateString('fr-FR', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
            });
            
            // Calculate estimated delivery (5 days from order)
            const estimatedDate = new Date(orderDate);
            estimatedDate.setDate(estimatedDate.getDate() + 5);
            const formattedEstimated = estimatedDate.toLocaleDateString('fr-FR', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
            });
            
            // Map items
            const items = order.items.map(item => ({
                name: item.product_name,
                quantity: item.quantity,
                price: formatOrderPrice(getItemLineTotal(item))
            }));
            
            setOrderStatus({
                number: order.order_number,
                status: getStatusLabel(order.status),
                date: formattedDate,
                estimatedDelivery: formattedEstimated,
                items: items,
                total: formatOrderPrice(getOrderTotals(order).grandTotal),
                steps: getStepsFromStatus(order.status, order.created_at),
                // Additional info
                customerName: `${order.first_name} ${order.last_name}`,
                address: order.address,
                city: order.city,
                phone: order.phone
            });
        } catch (err) {
            console.error('Error fetching order:', err);
            if (err.response?.status === 404) {
                setError('Commande non trouvée. Vérifiez le numéro de commande.');
            } else {
                setError('Une erreur est survenue. Veuillez réessayer.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section className="track-order py-80">
            <div className="container container-lg">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        <div className="text-center mb-48">
                            <h3 className="mb-16">Suivre ma commande</h3>
                            <p className="text-gray-700">
                                Entrez votre numéro de commande pour suivre son statut en temps réel
                            </p>
                        </div>

                        {/* Search Form */}
                        <div className="bg-color-three rounded-16 p-32 mb-48">
                            <form onSubmit={handleSubmit}>
                                <div className="row gy-4">
                                    <div className="col-12">
                                        <label
                                            htmlFor="orderNumber"
                                            className="text-sm font-heading-two text-gray-900 fw-semibold mb-8"
                                        >
                                            Numéro de commande
                                        </label>
                                        <input
                                            type="text"
                                            className="common-input track-order-input px-16"
                                            id="orderNumber"
                                            placeholder="Ex: CMD-2024-12345"
                                            value={orderNumber}
                                            onChange={(e) => setOrderNumber(e.target.value)}
                                            required
                                        />
                                    </div>
                                    
                                    {/* Error Message */}
                                    {error && (
                                        <div className="col-12">
                                            <div className="alert alert-danger d-flex align-items-center gap-8 py-12 px-16 rounded-8 mb-0" style={{ backgroundColor: '#fff5f5', border: '1px solid #feb2b2', color: '#c53030' }}>
                                                <i className="ph ph-warning-circle text-xl" />
                                                <span>{error}</span>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="col-12">
                                        <button
                                            type="submit"
                                            className="btn track-order-btn py-18 px-40 rounded-pill w-100"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? (
                                                <>
                                                    <i className="ph ph-spinner-gap animate-spin me-8" />
                                                    Recherche en cours...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ph ph-magnifying-glass me-8" />
                                                    Suivre ma commande
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Order Status */}
                        {orderStatus && (
                            <div className="order-status">
                                {/* Order Info */}
                                <div className="border border-gray-100 rounded-16 p-32 mb-32">
                                    <div className="flex-between mb-24 pb-24 border-bottom border-gray-100">
                                        <div>
                                            <h6 className="mb-8">Commande #{orderStatus.number}</h6>
                                            <p className="text-sm text-gray-600 mb-0">
                                                Passée le {orderStatus.date}
                                            </p>
                                        </div>
                                        <div className="text-end">
                                            <span className="badge track-order-status-badge py-8 px-24 rounded-pill">
                                                {orderStatus.status}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Tracking Steps */}
                                    <div className="tracking-steps mb-32">
                                        {orderStatus.steps.map((step, index) => (
                                            <div
                                                key={index}
                                                className={`tracking-step d-flex gap-24 mb-24 ${
                                                    index === orderStatus.steps.length - 1 ? 'mb-0' : ''
                                                }`}
                                            >
                                                <div className="position-relative">
                                                    <div
                                                        className={`w-40 h-40 rounded-circle flex-center flex-shrink-0 tracking-step-icon ${
                                                            step.completed
                                                                ? 'tracking-step-icon-completed'
                                                                : 'bg-gray-100 text-gray-500'
                                                        }`}
                                                    >
                                                        {step.completed ? (
                                                            <i className="ph-fill ph-check text-lg" />
                                                        ) : (
                                                            <i className="ph ph-circle text-lg" />
                                                        )}
                                                    </div>
                                                    {index < orderStatus.steps.length - 1 && (
                                                        <div
                                                            className={`position-absolute top-100 start-50 translate-middle-x tracking-step-line ${
                                                                step.completed
                                                                    ? 'tracking-step-line-completed'
                                                                    : 'bg-gray-200'
                                                            }`}
                                                            style={{
                                                                width: '2px',
                                                                height: '40px',
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6
                                                        className={`mb-4 ${
                                                            step.completed
                                                                ? 'text-gray-900'
                                                                : 'text-gray-500'
                                                        }`}
                                                    >
                                                        {step.title}
                                                    </h6>
                                                    <p className="text-sm text-gray-600 mb-0">
                                                        {step.date}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Estimated Delivery */}
                                    <div className="track-order-delivery rounded-8 p-16 flex-align gap-12">
                                        <i className="ph-fill ph-package text-2xl" />
                                        <div>
                                            <p className="text-sm text-gray-700 mb-0">
                                                <strong>Livraison estimée:</strong>{' '}
                                                {orderStatus.estimatedDelivery}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Order Items */}
                                <div className="border border-gray-100 rounded-16 p-32">
                                    <h6 className="mb-24 pb-24 border-bottom border-gray-100">
                                        Articles commandés
                                    </h6>
                                    {orderStatus.items.map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex-between mb-16"
                                        >
                                            <div>
                                                <p className="text-gray-900 mb-4">{item.name}</p>
                                                <p className="text-sm text-gray-600 mb-0">
                                                    Quantité: {item.quantity}
                                                </p>
                                            </div>
                                            <p className="text-gray-900 fw-semibold mb-0">
                                                {item.price}
                                            </p>
                                        </div>
                                    ))}
                                    <div className="border-top border-gray-100 mt-24 pt-24">
                                        <div className="flex-between">
                                            <h6 className="mb-0">Total</h6>
                                            <h6 className="mb-0 track-order-total">
                                                {orderStatus.total}
                                            </h6>
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="text-center mt-32">
                                    <p className="text-gray-700 mb-16">
                                        Besoin d'aide avec votre commande?
                                    </p>
                                    <Link
                                        to="/contact"
                                        className="btn track-order-btn py-12 px-32 rounded-pill"
                                    >
                                        <i className="ph ph-headset me-8" />
                                        Contacter le support
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* Help Section */}
                        {!orderStatus && (
                            <div className="text-center">
                                <div className="bg-gray-50 rounded-16 p-32">
                                    <i className="ph ph-info text-main-600 text-5xl mb-16" />
                                    <h6 className="mb-12">Comment trouver mon numéro de commande?</h6>
                                    <p className="text-gray-700 mb-0">
                                        Votre numéro de commande se trouve dans l'email de
                                        confirmation que vous avez reçu après votre achat. Il
                                        commence par "CMD-" suivi de l'année et d'un numéro unique.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TrackOrder;

